import { useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import ddt from '@wix/da-ddt';
import { MeasuredCookieType, ScreenSizeContext } from '../../Measured';
import { hasCookie } from '../../Measured/redux/selectors';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { DeviationTypes } from '../../types/deviation';
import variables from '@wix/da-ds/src/styles/variables.scss';
const reservedScreenHeightForGenericLayout = [
    'meta-above-fold',
    'actionbar-height-mobile',
    'site-header-height-mobile'
].map((prop)=>parseInt(variables[prop], 10)).reduce((sum, x)=>sum + x, 0);
const reservedScreenHeightForAdLayout = [
    'site-header-height-mobile'
].map((prop)=>parseInt(variables[prop], 10)).reduce((sum, x)=>sum + x, 0);
const logger = ddt.createLogger('artstage');
function useIsFirstDeviationAfterSSR(deviation) {
    // this is a bit wacky, but we want to ensure we never recalc the stage for the first deviation after SSR
    // so here we check if this was the first deviation rendered in the component's lifecycle
    // combined with the duperbrowse check, should be Close Enough(tm)
    // we also take note if cookies existed, so important to have this hook run before the cookies get updated
    const isFirstDeviation = useRef(true);
    const firstRenderDeviationId = useRef(deviation.deviationId);
    const hadCookie = useSelector((state)=>hasCookie(state, MeasuredCookieType.SCREEN_WIDTH));
    const firstDeviationHadCookie = useRef(hadCookie);
    if (isFirstDeviation.current && firstRenderDeviationId.current !== deviation.deviationId) {
        isFirstDeviation.current = false;
    }
    return {
        isFirstDeviation: isFirstDeviation.current,
        hadCookie: firstDeviationHadCookie.current
    };
}
function getReservedHeightForLayout(layout = 'generic', screenWidth) {
    switch(layout){
        case 'ads':
            // video ad does not have a set height. It has a 16:9 ratio
            // 16 for gap between image and ad and bottom of page
            // 55 for the adhesion unit
            return reservedScreenHeightForAdLayout + 16 + screenWidth / 16 * 9 + 55;
        case 'generic':
        default:
            return reservedScreenHeightForGenericLayout;
    }
}
function calculateFixedStageSize({ width: screenWidth, height: screenHeight = 640, layout }) {
    const targetHeight = screenHeight - getReservedHeightForLayout(layout, screenWidth);
    const minHeight = layout === 'ads' ? 130 : 230;
    const stageHeight = Math.max(targetHeight, minHeight);
    return {
        containerSize: {
            width: screenWidth,
            height: stageHeight
        },
        contentSize: {
            width: screenWidth,
            height: stageHeight
        }
    };
}
export function useMobileStage(deviation) {
    const { hadCookie, isFirstDeviation } = useIsFirstDeviationAfterSSR(deviation);
    const { height: screenHeight, width: screenWidth } = useContext(ScreenSizeContext);
    const deviationType = getDeviationType(deviation);
    const layout = 'generic';
    // we do not want to recalculate when the screen height changes because that
    // happens a lot, eg. browser toolbars hiding, keyboard etc. Screen width
    // change will still trigger a recalc which is good enough for stuff like rotation
    const initialScreenHeight = useRef(screenHeight);
    initialScreenHeight.current = screenHeight; // do keep it up to date though
    const stageSize = useMemo(()=>{
        // journals and commissions have a hardcoded height
        if ([
            DeviationTypes.JOURNAL,
            DeviationTypes.COMMISSION
        ].includes(deviationType)) {
            return {
                containerSize: {
                    width: screenWidth,
                    height: 300
                },
                contentSize: {
                    width: screenWidth,
                    height: 300
                }
            };
        }
        // First deviation without cookie must be the default stage size
        if (!hadCookie && isFirstDeviation) {
            logger.log('No cookie');
            return calculateFixedStageSize({
                width: screenWidth,
                layout
            });
        }
        return calculateFixedStageSize({
            width: screenWidth,
            height: initialScreenHeight.current,
            layout
        });
    }, [
        hadCookie,
        isFirstDeviation,
        screenWidth,
        deviationType,
        layout
    ]);
    return stageSize;
}
